import { BeLinkCOS } from '@be-link/cos';
import {
	Affix,
	Alert,
	Button,
	Card,
	Checkbox,
	Col,
	Descriptions,
	Drawer,
	Empty,
	Input,
	InputNumber,
	Modal,
	message,
	Popconfirm,
	Row,
	Select,
	Space,
	Spin,
	Switch,
	Table,
	Tabs,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
	ReloadOutlined,
	SaveOutlined,
	SettingOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatYmdHmsZh } from '../utils/formatDateTime';
import { type CosUploader, uploadTextToCosPath } from '../utils/cosUpload';

const { Paragraph, Text } = Typography;
const JSON_INDENT = 4;

type AppConfigEnv = 'test' | 'production';
type PushyPlatform = 'ios' | 'android' | 'harmony';
type UpdateMode = 'silent' | 'force';

const PUSHY_PLATFORMS: readonly PushyPlatform[] = [
	'ios',
	'android',
	'harmony',
] as const;

/** 未填写灰度时，新增（未应用）原生版本按平台的默认灰度比例 */
const DEFAULT_NEW_NATIVE_VERSION_ROLLOUT_BY_PLATFORM: Record<
	PushyPlatform,
	number
> = {
	ios: 10,
	android: 5,
	harmony: 5,
};

function getDefaultNewNativeVersionRollout(platform: PushyPlatform): number {
	return DEFAULT_NEW_NATIVE_VERSION_ROLLOUT_BY_PLATFORM[platform];
}

type AppConfigEnvOption = {
	label: string;
	value: AppConfigEnv;
	url: string;
	tagColor: string;
};

type RemoteConfig = Record<string, unknown> & {
	pushyHotUpdate?: PushyHotUpdateManifest;
};

type PushyHotUpdateManifest = {
	enabled?: boolean | number | string;
	checkIntervalSeconds?: number;
	forceCheckToken?: string;
	ios?: PushyHotUpdatePlatformConfig;
	android?: PushyHotUpdatePlatformConfig;
	harmony?: PushyHotUpdatePlatformConfig;
	/** 仅用于把老配置可视化迁移到平台分组，保存时不会再写回 */
	items?: LegacyPushyHotUpdateItem[];
};

type PushyHotUpdatePlatformConfig = {
	enabled?: boolean | number | string;
	checkIntervalSeconds?: number;
	forceCheckToken?: string;
	items?: PushyHotUpdateItem[];
};

type PushyHotUpdateItem = {
	enabled?: boolean | number | string;
	targetNativeVersions?: string[];
	targetPatchName?: string;
	updateMode?: UpdateMode | string;
	checkIntervalSeconds?: number;
	forceCheckToken?: string;
};

type LegacyPushyHotUpdateItem = PushyHotUpdateItem & {
	platform?: PushyPlatform | string;
};

type EditableHotUpdateManifest = {
	enabled: boolean;
	checkIntervalSeconds?: number;
	forceCheckToken: string;
	ios: EditablePlatformConfig;
	android: EditablePlatformConfig;
	harmony: EditablePlatformConfig;
};

type EditablePlatformConfig = {
	enabled: boolean;
	checkIntervalSeconds?: number;
	forceCheckToken: string;
	items: EditableHotUpdateItem[];
};

type NativeVersionBindingInfo = {
	packageId: number | string;
	bindingId?: number | string;
	rollout?: number;
	type?: string;
};

type EditableHotUpdateItem = {
	id: string;
	enabled: boolean;
	targetNativeVersions: string[];
	targetPatchName: string;
	updateMode: UpdateMode;
	checkIntervalSeconds?: number;
	forceCheckToken: string;
	description?: string;
	createdAt?: string;
	bindingTypes?: string[];
	sourceVersionId?: string;
	nativeVersionBindings?: Record<string, NativeVersionBindingInfo>;
};

type EnvState = {
	loading: boolean;
	saving: boolean;
	loaded: boolean;
	lastModified: string;
	fullConfig: RemoteConfig | null;
	hotUpdate: EditableHotUpdateManifest;
	lastSavedHotUpdate: string;
};

type NativePackageOption = {
	label: string;
	value: string;
};

type NativePackageState = {
	loading: boolean;
	loaded: boolean;
	options: NativePackageOption[];
	packages: PushyNativePackage[];
	bindings: PushyBindingRecord[];
	error: string;
};

type PushyNativePackage = {
	id: number | string;
	name?: string;
	status?: string;
	buildTime?: string;
	bindings?: PushyNativePackageBinding[];
};

type PushyNativePackageBinding = {
	id?: number | string;
	type?: 'exp' | 'full' | string;
	rollout?: number;
	versionId?: number | string;
	versionName?: string;
	versionHash?: string;
	version?: {
		id?: number | string;
		name?: string;
		hash?: string;
	};
	versions?: {
		id?: number | string;
		name?: string;
		hash?: string;
	};
};

type PushyPackageListResponse = {
	data?: PushyNativePackage[];
};

type PushyBindingRecord = {
	id: number | string;
	versionId: number | string;
	packageId: number | string;
	rollout: number | null;
};

type PushyBindingListResponse = {
	data?: PushyBindingRecord[];
};

type PushyHotUpdateVersion = {
	id: number | string;
	hash?: string;
	name?: string;
	description?: string;
	metaInfo?: unknown;
	createdAt?: string;
};

type PushyVersionListResponse = {
	data?: PushyHotUpdateVersion[];
};

const APP_CONFIG_ENV_OPTIONS: AppConfigEnvOption[] = [
	{
		label: '测试环境',
		value: 'test',
		url: 'https://cdn.8848top.com/pandora-miniprogram/app-update/dev/config.json',
		tagColor: 'gold',
	},
	{
		label: '生产环境',
		value: 'production',
		url: 'https://cdn.8848top.com/pandora-miniprogram/app-update/prod/config.json',
		tagColor: 'red',
	},
];

const PUSHY_PACKAGE_APP_IDS: Record<
	AppConfigEnv,
	Record<PushyPlatform, number>
> = {
	test: {
		ios: 32263,
		android: 32264,
		harmony: 32328,
	},
	production: {
		ios: 31976,
		android: 31975,
		harmony: 32320,
	},
};

const PUSHY_API_TOKEN =
	'pushy_m_s-T5rtcIZlW_HSfSGGcQFuEGY-iQ4FdFF5cyaUXqVj_jce';
const PUSHY_ACCESS_TOKEN =
	typeof import.meta.env.PUBLIC_PUSHY_ACCESS_TOKEN === 'string'
		? import.meta.env.PUBLIC_PUSHY_ACCESS_TOKEN.trim()
		: '';
const PUSHY_API_BASE_URL = 'https://update.reactnative.cn/api';
const PUSHY_PACKAGE_LIST_LIMIT = 1000;
const PUSHY_VERSION_LIST_LIMIT = 15;

const PLATFORM_META: Record<PushyPlatform, { label: string; color: string }> = {
	ios: {
		label: 'iOS',
		color: 'blue',
	},
	android: {
		label: 'Android',
		color: 'green',
	},
	harmony: {
		label: 'HarmonyOS',
		color: 'volcano',
	},
};

const UPDATE_MODE_OPTIONS: { label: string; value: UpdateMode }[] = [
	{ label: '静默更新（只冷启动检查）', value: 'silent' },
	{ label: '强制更新（冷启动 + 回前台检查）', value: 'force' },
];

function createItemId() {
	return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function createEmptyHotUpdate(): EditableHotUpdateManifest {
	return {
		enabled: true,
		checkIntervalSeconds: 21600,
		forceCheckToken: '',
		ios: createEmptyPlatformConfig(),
		android: createEmptyPlatformConfig(),
		harmony: createEmptyPlatformConfig(),
	};
}

function createEmptyPlatformConfig(): EditablePlatformConfig {
	return {
		enabled: true,
		checkIntervalSeconds: undefined,
		forceCheckToken: '',
		items: [],
	};
}

function createEmptyItem(): EditableHotUpdateItem {
	return {
		id: createItemId(),
		enabled: true,
		targetNativeVersions: [],
		targetPatchName: '',
		updateMode: 'silent',
		checkIntervalSeconds: undefined,
		forceCheckToken: '',
		description: '',
		createdAt: '',
		bindingTypes: [],
		sourceVersionId: '',
	};
}

function getErrorMessage(error: unknown) {
	if (error instanceof Error) {
		return error.message;
	}

	return '未知错误';
}

function coerceRemoteBoolean(value: unknown, fallback = true) {
	if (value == null) {
		return fallback;
	}
	if (value === true || value === 1) {
		return true;
	}
	if (value === false || value === 0) {
		return false;
	}
	if (typeof value === 'string') {
		const normalized = value.trim().toLowerCase();
		if (['true', '1', 'yes'].includes(normalized)) {
			return true;
		}
		if (['false', '0', 'no'].includes(normalized)) {
			return false;
		}
	}

	return fallback;
}

function normalizeString(value: unknown) {
	return typeof value === 'string' ? value.trim() : '';
}

function normalizeNumber(value: unknown): number | undefined {
	return typeof value === 'number' && Number.isFinite(value) && value >= 0
		? value
		: undefined;
}

function normalizeNumberInput(
	value: number | string | null,
): number | undefined {
	if (typeof value === 'number') {
		return Number.isFinite(value) && value >= 0 ? value : undefined;
	}
	if (typeof value === 'string' && value.trim()) {
		const parsed = Number(value);
		return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
	}
	return undefined;
}

function normalizeVersionList(value: unknown): string[] {
	if (Array.isArray(value)) {
		return value.map((item) => normalizeString(item)).filter(Boolean);
	}
	if (typeof value === 'string') {
		return value
			.split(/[\n,，]/u)
			.map((item) => item.trim())
			.filter(Boolean);
	}
	return [];
}

function normalizeUpdateMode(value: unknown): UpdateMode {
	return normalizeString(value).toLowerCase() === 'force' ? 'force' : 'silent';
}

function parseMetaInfo(raw: unknown): Record<string, unknown> | null {
	if (!raw) {
		return null;
	}
	if (typeof raw === 'object' && !Array.isArray(raw)) {
		return raw as Record<string, unknown>;
	}
	if (typeof raw === 'string') {
		try {
			const parsed = JSON.parse(raw);
			return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
				? (parsed as Record<string, unknown>)
				: null;
		} catch {
			return null;
		}
	}
	return null;
}

function isForceUpdateMetaInfo(raw: unknown) {
	const metaInfo = parseMetaInfo(raw);
	const value = metaInfo?.forceUpdate;
	if (value === true || value === 1) {
		return true;
	}
	if (typeof value === 'string') {
		const normalized = value.trim().toLowerCase();
		return normalized === 'true' || normalized === '1';
	}
	return false;
}

function normalizeItem(
	item: PushyHotUpdateItem | LegacyPushyHotUpdateItem,
): EditableHotUpdateItem {
	return {
		id: createItemId(),
		enabled: coerceRemoteBoolean(item.enabled, true),
		targetNativeVersions: normalizeVersionList(item.targetNativeVersions),
		targetPatchName: normalizeString(item.targetPatchName),
		updateMode: normalizeUpdateMode(item.updateMode),
		checkIntervalSeconds: normalizeNumber(item.checkIntervalSeconds),
		forceCheckToken: normalizeString(item.forceCheckToken),
		description: '',
		createdAt: '',
		bindingTypes: [],
		sourceVersionId: '',
	};
}

function normalizePlatformConfig(
	config: PushyHotUpdatePlatformConfig | undefined,
	legacyItems: LegacyPushyHotUpdateItem[],
): EditablePlatformConfig {
	const directItems = Array.isArray(config?.items) ? config.items : [];
	const sourceItems = directItems.length > 0 ? directItems : legacyItems;

	return {
		enabled: coerceRemoteBoolean(config?.enabled, true),
		checkIntervalSeconds: normalizeNumber(config?.checkIntervalSeconds),
		forceCheckToken: normalizeString(config?.forceCheckToken),
		items: sourceItems.map(normalizeItem),
	};
}

function normalizeHotUpdateManifest(
	manifest: PushyHotUpdateManifest | undefined,
): EditableHotUpdateManifest {
	const legacyItems = Array.isArray(manifest?.items) ? manifest.items : [];
	const legacyByPlatform: Record<PushyPlatform, LegacyPushyHotUpdateItem[]> = {
		ios: [],
		android: [],
		harmony: [],
	};

	for (const item of legacyItems) {
		const platform = normalizeString(item.platform) as PushyPlatform;
		if (
			platform === 'ios' ||
			platform === 'android' ||
			platform === 'harmony'
		) {
			legacyByPlatform[platform].push(item);
		}
	}

	return {
		enabled: coerceRemoteBoolean(manifest?.enabled, false),
		checkIntervalSeconds: normalizeNumber(manifest?.checkIntervalSeconds),
		forceCheckToken: normalizeString(manifest?.forceCheckToken),
		ios: normalizePlatformConfig(manifest?.ios, legacyByPlatform.ios),
		android: normalizePlatformConfig(
			manifest?.android,
			legacyByPlatform.android,
		),
		harmony: normalizePlatformConfig(
			manifest?.harmony,
			legacyByPlatform.harmony,
		),
	};
}

function buildItemForSave(item: EditableHotUpdateItem): PushyHotUpdateItem {
	const result: PushyHotUpdateItem = {
		enabled: item.enabled,
		targetNativeVersions: item.targetNativeVersions,
		targetPatchName: item.targetPatchName.trim(),
		updateMode: item.updateMode,
	};

	if (item.checkIntervalSeconds != null) {
		result.checkIntervalSeconds = item.checkIntervalSeconds;
	}
	if (item.forceCheckToken.trim()) {
		result.forceCheckToken = item.forceCheckToken.trim();
	}

	return result;
}

function buildPlatformForSave(
	config: EditablePlatformConfig,
): PushyHotUpdatePlatformConfig {
	const result: PushyHotUpdatePlatformConfig = {
		enabled: config.enabled,
		items: config.items.map(buildItemForSave),
	};

	if (config.checkIntervalSeconds != null) {
		result.checkIntervalSeconds = config.checkIntervalSeconds;
	}
	if (config.forceCheckToken.trim()) {
		result.forceCheckToken = config.forceCheckToken.trim();
	}

	return result;
}

function buildManifestForSave(
	config: EditableHotUpdateManifest,
): PushyHotUpdateManifest {
	const result: PushyHotUpdateManifest = {
		enabled: config.enabled,
		ios: buildPlatformForSave(config.ios),
		android: buildPlatformForSave(config.android),
		harmony: buildPlatformForSave(config.harmony),
	};

	if (config.checkIntervalSeconds != null) {
		result.checkIntervalSeconds = config.checkIntervalSeconds;
	}
	if (config.forceCheckToken.trim()) {
		result.forceCheckToken = config.forceCheckToken.trim();
	}

	return result;
}

function serializeHotUpdate(config: EditableHotUpdateManifest) {
	return JSON.stringify(buildManifestForSave(config));
}

function createInitialEnvState(): EnvState {
	const hotUpdate = createEmptyHotUpdate();
	return {
		loading: false,
		saving: false,
		loaded: false,
		lastModified: '',
		fullConfig: null,
		hotUpdate,
		lastSavedHotUpdate: serializeHotUpdate(hotUpdate),
	};
}

function createNativePackageState(): NativePackageState {
	return {
		loading: false,
		loaded: false,
		options: [],
		packages: [],
		bindings: [],
		error: '',
	};
}

function createInitialNativePackageStates(): Record<
	AppConfigEnv,
	Record<PushyPlatform, NativePackageState>
> {
	return {
		test: {
			ios: createNativePackageState(),
			android: createNativePackageState(),
			harmony: createNativePackageState(),
		},
		production: {
			ios: createNativePackageState(),
			android: createNativePackageState(),
			harmony: createNativePackageState(),
		},
	};
}

function normalizeNativePackageOptions(
	packages: PushyNativePackage[] | undefined,
): NativePackageOption[] {
	const seen = new Set<string>();
	const options: NativePackageOption[] = [];

	for (const pkg of packages || []) {
		const name = normalizeString(pkg.name);
		if (!name || seen.has(name)) {
			continue;
		}
		seen.add(name);
		options.push({
			value: name,
			label:
				pkg.status && pkg.status !== 'normal'
					? `${name}（${pkg.status}）`
					: name,
		});
	}

	return options;
}

async function fetchPushyNativePackageOptions(
	env: AppConfigEnv,
	platform: PushyPlatform,
): Promise<{
	options: NativePackageOption[];
	packages: PushyNativePackage[];
}> {
	const appId = PUSHY_PACKAGE_APP_IDS[env][platform];
	const url = `${PUSHY_API_BASE_URL}/app/${appId}/package/list?limit=${PUSHY_PACKAGE_LIST_LIMIT}&offset=0`;
	const response = await fetch(url, {
		headers: {
			'x-api-token': PUSHY_API_TOKEN,
		},
	});

	if (!response.ok) {
		throw new Error(`Pushy 原生包列表读取失败，状态码：${response.status}`);
	}

	const data = (await response.json()) as PushyPackageListResponse;
	const packages = Array.isArray(data.data) ? data.data : [];
	return {
		options: normalizeNativePackageOptions(packages),
		packages,
	};
}

async function fetchPushyHotUpdateVersions(
	env: AppConfigEnv,
	platform: PushyPlatform,
): Promise<PushyHotUpdateVersion[]> {
	const appId = PUSHY_PACKAGE_APP_IDS[env][platform];
	const url = `${PUSHY_API_BASE_URL}/app/${appId}/version/list?limit=${PUSHY_VERSION_LIST_LIMIT}&offset=0`;
	const response = await fetch(url, {
		headers: {
			'x-api-token': PUSHY_API_TOKEN,
		},
	});

	if (!response.ok) {
		throw new Error(`Pushy 热更新包列表读取失败，状态码：${response.status}`);
	}

	const data = (await response.json()) as PushyVersionListResponse;
	return Array.isArray(data.data) ? data.data : [];
}

async function fetchPushyBindings(
	env: AppConfigEnv,
	platform: PushyPlatform,
): Promise<PushyBindingRecord[]> {
	const appId = PUSHY_PACKAGE_APP_IDS[env][platform];
	const response = await fetch(`${PUSHY_API_BASE_URL}/app/${appId}/binding`, {
		headers: getPushyAuthHeaders(),
	});

	if (!response.ok) {
		throw new Error(`Pushy 绑定列表读取失败，状态码：${response.status}`);
	}

	const data = (await response.json()) as PushyBindingListResponse;
	return Array.isArray(data.data) ? data.data : [];
}

function getPushyAuthHeaders(): Record<string, string> {
	const headers: Record<string, string> = {
		accept: '*/*',
	};
	if (PUSHY_ACCESS_TOKEN) {
		headers['x-accesstoken'] = PUSHY_ACCESS_TOKEN;
	} else {
		headers['x-api-token'] = PUSHY_API_TOKEN;
	}
	return headers;
}

function getPushyWriteHeaders(): Record<string, string> {
	return {
		...getPushyAuthHeaders(),
		'Content-Type': 'application/json',
	};
}

function normalizePushyBindingRollout(
	rollout: number | null | undefined,
): number | undefined {
	if (typeof rollout === 'number' && Number.isFinite(rollout)) {
		return rollout;
	}
	if (rollout === null) {
		return 100;
	}
	return undefined;
}

function createBindingInfoFromRecord(
	record: PushyBindingRecord,
): NativeVersionBindingInfo {
	const rollout = normalizePushyBindingRollout(record.rollout);
	return {
		packageId: record.packageId,
		bindingId: record.id,
		rollout,
		type: record.rollout == null ? 'full' : 'exp',
	};
}

function buildPackageIdToNameMap(
	packages: PushyNativePackage[],
): Map<string, string> {
	const map = new Map<string, string>();
	for (const pkg of packages) {
		const name = normalizeString(pkg.name);
		if (name && pkg.id != null) {
			map.set(String(pkg.id), name);
		}
	}
	return map;
}

function normalizeBindingRollout(binding: PushyNativePackageBinding): number | undefined {
	if (typeof binding.rollout === 'number' && Number.isFinite(binding.rollout)) {
		return binding.rollout;
	}
	if (binding.type === 'full') {
		return 100;
	}
	return undefined;
}

function createNativeVersionBindingInfo(
	pkg: PushyNativePackage,
	binding: PushyNativePackageBinding,
): NativeVersionBindingInfo {
	return {
		packageId: pkg.id ?? '',
		bindingId: binding.id,
		rollout: normalizeBindingRollout(binding),
		type: binding.type,
	};
}

function findBindingForHotUpdateItem(
	pkg: PushyNativePackage,
	item: Pick<EditableHotUpdateItem, 'sourceVersionId' | 'targetPatchName'>,
): PushyNativePackageBinding | undefined {
	return (pkg.bindings || []).find((binding) => {
		if (
			item.sourceVersionId &&
			String(binding.versionId || '') === item.sourceVersionId
		) {
			return true;
		}
		const boundVersion = binding.versions || binding.version;
		if (
			item.targetPatchName &&
			boundVersion?.name &&
			boundVersion.name === item.targetPatchName
		) {
			return true;
		}
		if (
			item.targetPatchName &&
			binding.versionName &&
			binding.versionName === item.targetPatchName
		) {
			return true;
		}
		return false;
	});
}

function getItemNativeBindings(
	item: EditableHotUpdateItem,
	packages: PushyNativePackage[],
	pushyBindings: PushyBindingRecord[] = [],
): Record<string, NativeVersionBindingInfo> {
	const result: Record<string, NativeVersionBindingInfo> = {};
	const packageIdToName = buildPackageIdToNameMap(packages);
	const versionId = normalizeString(item.sourceVersionId);

	if (versionId && pushyBindings.length > 0) {
		for (const record of pushyBindings) {
			if (String(record.versionId) !== versionId) {
				continue;
			}
			const nativeVersion = packageIdToName.get(String(record.packageId));
			if (!nativeVersion || !item.targetNativeVersions.includes(nativeVersion)) {
				continue;
			}
			result[nativeVersion] = createBindingInfoFromRecord(record);
		}
	}

	for (const nativeVersion of item.targetNativeVersions) {
		if (result[nativeVersion]) {
			continue;
		}

		const pkg = packages.find(
			(entry) => normalizeString(entry.name) === nativeVersion,
		);
		if (!pkg || pkg.id == null) {
			continue;
		}

		const binding = findBindingForHotUpdateItem(pkg, item);
		if (binding) {
			result[nativeVersion] = createNativeVersionBindingInfo(pkg, binding);
			continue;
		}

		result[nativeVersion] = { packageId: pkg.id };
	}

	return result;
}

function getNativeVersionBindingInfoForItem(
	item: EditableHotUpdateItem,
	nativeVersion: string,
	packages: PushyNativePackage[],
	pushyBindings: PushyBindingRecord[] = [],
): NativeVersionBindingInfo | undefined {
	const pkg = packages.find(
		(entry) => normalizeString(entry.name) === nativeVersion,
	);
	if (!pkg || pkg.id == null) {
		return undefined;
	}

	const versionId = normalizeString(item.sourceVersionId);
	if (versionId && pushyBindings.length > 0) {
		const record = pushyBindings.find(
			(entry) =>
				String(entry.versionId) === versionId &&
				String(entry.packageId) === String(pkg.id),
		);
		if (record) {
			return createBindingInfoFromRecord(record);
		}
	}

	const binding = findBindingForHotUpdateItem(pkg, item);
	if (binding) {
		return createNativeVersionBindingInfo(pkg, binding);
	}

	return { packageId: pkg.id };
}

function buildNativeVersionSelectOptions(
	item: EditableHotUpdateItem,
	packageOptions: NativePackageOption[],
	packages: PushyNativePackage[],
	pushyBindings: PushyBindingRecord[],
) {
	return packageOptions.map((option) => ({
		value: option.value,
		label: formatNativeVersionTagLabel(
			option.value,
			getNativeVersionBindingInfoForItem(
				item,
				option.value,
				packages,
				pushyBindings,
			),
		),
	}));
}

function formatNativeVersionTagLabel(
	nativeVersion: string,
	bindingInfo?: NativeVersionBindingInfo,
): string {
	if (!bindingInfo) {
		return nativeVersion;
	}
	if (bindingInfo.rollout != null) {
		return `${nativeVersion}（${bindingInfo.rollout}%）`;
	}
	if (bindingInfo.type === 'full') {
		return `${nativeVersion}（100%）`;
	}
	return nativeVersion;
}

async function createOrUpdatePushyBinding(
	env: AppConfigEnv,
	platform: PushyPlatform,
	payload: {
		versionId: number | string;
		rollout: number;
		packageId: number | string;
	},
) {
	const appId = PUSHY_PACKAGE_APP_IDS[env][platform];
	const response = await fetch(`${PUSHY_API_BASE_URL}/app/${appId}/binding/`, {
		method: 'POST',
		headers: getPushyWriteHeaders(),
		body: JSON.stringify(payload),
	});

	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(
			errorText.trim() || `设置灰度失败，状态码：${response.status}`,
		);
	}
}

async function deletePushyBinding(
	env: AppConfigEnv,
	platform: PushyPlatform,
	bindingId: number | string,
) {
	const appId = PUSHY_PACKAGE_APP_IDS[env][platform];
	const response = await fetch(
		`${PUSHY_API_BASE_URL}/app/${appId}/binding/${bindingId}`,
		{
			method: 'DELETE',
			headers: getPushyWriteHeaders(),
		},
	);

	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(
			errorText.trim() || `取消发布失败，状态码：${response.status}`,
		);
	}
}

async function resolveHotUpdateVersionId(
	env: AppConfigEnv,
	platform: PushyPlatform,
	item: EditableHotUpdateItem,
	packages: PushyNativePackage[],
): Promise<string> {
	if (item.sourceVersionId) {
		return item.sourceVersionId;
	}

	for (const nativeVersion of item.targetNativeVersions) {
		const pkg = packages.find(
			(entry) => normalizeString(entry.name) === nativeVersion,
		);
		if (!pkg) {
			continue;
		}

		const binding = findBindingForHotUpdateItem(pkg, item);
		if (binding?.versionId) {
			return String(binding.versionId);
		}

		const boundVersion = binding?.versions || binding?.version;
		if (boundVersion?.id) {
			return String(boundVersion.id);
		}
	}

	const patchName = normalizeString(item.targetPatchName);
	if (!patchName) {
		throw new Error('缺少热更版本名称，请先同步 Pushy 数据');
	}

	const versions = await fetchPushyHotUpdateVersions(env, platform);
	const matchedVersion = versions.find(
		(version) => normalizeString(version.name) === patchName,
	);
	if (!matchedVersion?.id) {
		throw new Error(`找不到热更版本「${patchName}」的 Pushy ID，请先同步 Pushy 数据`);
	}

	return String(matchedVersion.id);
}

function isValidRolloutPercent(
	value: number | undefined,
): value is number {
	return (
		typeof value === 'number' &&
		Number.isInteger(value) &&
		value >= 0 &&
		value <= 100
	);
}

function hasUnpublishedNativeVersions(
	item: EditableHotUpdateItem,
	packages: PushyNativePackage[],
	pushyBindings: PushyBindingRecord[] = [],
): boolean {
	const bindings = getItemNativeBindings(item, packages, pushyBindings);
	return item.targetNativeVersions.some(
		(nativeVersion) => !bindings[nativeVersion]?.bindingId,
	);
}

function canApplyRollout(
	item: EditableHotUpdateItem,
	draftRollout: number | undefined,
	packages: PushyNativePackage[],
	pushyBindings: PushyBindingRecord[] = [],
): boolean {
	if (item.targetNativeVersions.length === 0) {
		return false;
	}
	if (isValidRolloutPercent(draftRollout)) {
		return true;
	}
	return hasUnpublishedNativeVersions(item, packages, pushyBindings);
}

function resolveRolloutForNativeVersion(
	platform: PushyPlatform,
	draftRollout: number | undefined,
	isNewNativeVersion: boolean,
): number | undefined {
	if (isValidRolloutPercent(draftRollout)) {
		return draftRollout;
	}
	if (isNewNativeVersion) {
		return getDefaultNewNativeVersionRollout(platform);
	}
	return undefined;
}

function normalizeRolloutInput(
	value: number | string | null,
): number | undefined {
	if (value == null || value === '') {
		return undefined;
	}
	const num = typeof value === 'number' ? value : Number(value);
	if (!Number.isFinite(num)) {
		return undefined;
	}
	const rounded = Math.round(num);
	if (rounded < 0 || rounded > 100) {
		return undefined;
	}
	return rounded;
}

async function applyPushyRolloutForItem(
	env: AppConfigEnv,
	platform: PushyPlatform,
	item: EditableHotUpdateItem,
	draftRollout: number | undefined,
	packages: PushyNativePackage[],
	pushyBindings: PushyBindingRecord[] = [],
): Promise<{ appliedCount: number; appliedRollouts: number[] }> {
	const versionId = await resolveHotUpdateVersionId(env, platform, item, packages);
	if (item.targetNativeVersions.length === 0) {
		throw new Error('当前没有绑定的原生版本');
	}

	const bindings = getItemNativeBindings(item, packages, pushyBindings);
	const errors: string[] = [];
	const appliedRollouts = new Set<number>();
	let appliedCount = 0;

	for (const nativeVersion of item.targetNativeVersions) {
		const isNewNativeVersion = !bindings[nativeVersion]?.bindingId;
		const rollout = resolveRolloutForNativeVersion(
			platform,
			draftRollout,
			isNewNativeVersion,
		);
		if (!isValidRolloutPercent(rollout)) {
			continue;
		}

		const pkg = packages.find(
			(entry) => normalizeString(entry.name) === nativeVersion,
		);
		if (!pkg?.id) {
			errors.push(`${nativeVersion}：找不到原生包`);
			continue;
		}

		try {
			await createOrUpdatePushyBinding(env, platform, {
				versionId,
				rollout,
				packageId: pkg.id,
			});
			appliedCount += 1;
			appliedRollouts.add(rollout);
		} catch (error) {
			errors.push(`${nativeVersion}：${getErrorMessage(error)}`);
		}
	}

	if (appliedCount === 0 && errors.length === 0) {
		throw new Error('灰度比例需为 0-100 之间的正整数');
	}

	if (errors.length > 0) {
		throw new Error(errors.join('；'));
	}

	return {
		appliedCount,
		appliedRollouts: Array.from(appliedRollouts),
	};
}

async function cancelPushyPublishForNativeVersions(
	env: AppConfigEnv,
	platform: PushyPlatform,
	item: EditableHotUpdateItem,
	nativeVersions: string[],
	packages: PushyNativePackage[],
	pushyBindings: PushyBindingRecord[] = [],
) {
	const bindings = getItemNativeBindings(item, packages, pushyBindings);
	const errors: string[] = [];

	for (const nativeVersion of nativeVersions) {
		const bindingInfo = bindings[nativeVersion];
		if (!bindingInfo?.bindingId) {
			continue;
		}

		try {
			await deletePushyBinding(env, platform, bindingInfo.bindingId);
		} catch (error) {
			errors.push(`${nativeVersion}：${getErrorMessage(error)}`);
		}
	}

	if (errors.length > 0) {
		throw new Error(errors.join('；'));
	}
}

async function cancelPushyPublishForItem(
	env: AppConfigEnv,
	platform: PushyPlatform,
	item: EditableHotUpdateItem,
	packages: PushyNativePackage[],
	pushyBindings: PushyBindingRecord[] = [],
) {
	const bindings = getItemNativeBindings(item, packages, pushyBindings);
	const cancelableVersions = item.targetNativeVersions.filter(
		(nativeVersion) => !!bindings[nativeVersion]?.bindingId,
	);

	if (cancelableVersions.length === 0) {
		throw new Error('未找到可取消的绑定');
	}

	await cancelPushyPublishForNativeVersions(
		env,
		platform,
		item,
		cancelableVersions,
		packages,
		pushyBindings,
	);
}

function doesBindingMatchVersion(
	binding: PushyNativePackageBinding,
	version: PushyHotUpdateVersion,
) {
	const boundVersion = binding.versions || binding.version;
	if (!boundVersion) {
		return (
			String(binding.versionId || '') === String(version.id || '') ||
			(!!binding.versionHash &&
				!!version.hash &&
				binding.versionHash === version.hash) ||
			(!!binding.versionName &&
				!!version.name &&
				binding.versionName === version.name)
		);
	}
	return (
		String(boundVersion.id || '') === String(version.id || '') ||
		(!!boundVersion.hash &&
			!!version.hash &&
			boundVersion.hash === version.hash) ||
		(!!boundVersion.name &&
			!!version.name &&
			boundVersion.name === version.name)
	);
}

function buildHotUpdateItemsFromPushy(
	versions: PushyHotUpdateVersion[],
	packages: PushyNativePackage[],
	pushyBindings: PushyBindingRecord[],
	previousItems: EditableHotUpdateItem[],
): EditableHotUpdateItem[] {
	const packageIdToName = buildPackageIdToNameMap(packages);
	const previousItemByPatchName = new Map(
		previousItems.map((item) => [item.targetPatchName, item]),
	);
	const sortedVersions = versions
		.map((version, index) => ({ version, index }))
		.sort((left, right) => {
			const leftTime = Date.parse(normalizeString(left.version.createdAt));
			const rightTime = Date.parse(normalizeString(right.version.createdAt));
			if (Number.isFinite(leftTime) && Number.isFinite(rightTime)) {
				return rightTime - leftTime;
			}
			return left.index - right.index;
		})
		.map((item) => item.version);

	return sortedVersions
		.map((version) => {
			const targetPatchName = normalizeString(version.name);
			const previousItem = previousItemByPatchName.get(targetPatchName);
			const updateMode: UpdateMode = isForceUpdateMetaInfo(version.metaInfo)
				? 'force'
				: 'silent';
			const nativeVersions: string[] = [];
			const bindingTypes = new Set<string>();
			const nativeVersionBindings: Record<string, NativeVersionBindingInfo> =
				{};
			const versionId = String(version.id || '');

			if (versionId && pushyBindings.length > 0) {
				for (const record of pushyBindings) {
					if (String(record.versionId) !== versionId) {
						continue;
					}
					const nativeVersion = packageIdToName.get(String(record.packageId));
					if (!nativeVersion || nativeVersionBindings[nativeVersion]) {
						continue;
					}

					nativeVersions.push(nativeVersion);
					nativeVersionBindings[nativeVersion] =
						createBindingInfoFromRecord(record);
					bindingTypes.add(record.rollout == null ? 'full' : 'exp');
				}
			}

			for (const pkg of packages) {
				const nativeVersion = normalizeString(pkg.name);
				if (!nativeVersion || nativeVersionBindings[nativeVersion]) {
					continue;
				}

				const matchedBinding = (pkg.bindings || []).find((binding) =>
					doesBindingMatchVersion(binding, version),
				);
				if (!matchedBinding) {
					continue;
				}

				nativeVersions.push(nativeVersion);
				nativeVersionBindings[nativeVersion] = createNativeVersionBindingInfo(
					pkg,
					matchedBinding,
				);
				if (matchedBinding.type) {
					bindingTypes.add(matchedBinding.type);
				}
			}

			return {
				...(previousItem || createEmptyItem()),
				id: previousItem?.id || createItemId(),
				enabled: nativeVersions.length > 0,
				targetNativeVersions: nativeVersions,
				targetPatchName,
				updateMode,
				checkIntervalSeconds: previousItem?.checkIntervalSeconds,
				forceCheckToken: previousItem?.forceCheckToken || '',
				description: normalizeString(version.description),
				createdAt: normalizeString(version.createdAt),
				bindingTypes: Array.from(bindingTypes),
				sourceVersionId: String(version.id || ''),
				nativeVersionBindings,
			};
		})
		.filter((item) => item.targetPatchName);
}

function formatConfigForUpload(config: RemoteConfig) {
	return `${JSON.stringify(config, null, JSON_INDENT)}\n`;
}

function validateHotUpdate(config: EditableHotUpdateManifest): string | null {
	for (const platform of PUSHY_PLATFORMS) {
		for (const [index, item] of config[platform].items.entries()) {
			const prefix = `${PLATFORM_META[platform].label} 第 ${index + 1} 个热更新包`;
			if (item.enabled && item.targetNativeVersions.length === 0) {
				return `${prefix} 需要至少配置一个原生版本`;
			}
			if (item.enabled && !item.targetPatchName.trim()) {
				return `${prefix} 需要填写目标热更版本名称`;
			}
		}
	}

	return null;
}

function getNativeVersionOptionLabel(
	nativeVersion: string,
	options: { label: string; value: string }[],
): string {
	return (
		options.find((option) => option.value === nativeVersion)?.label ||
		nativeVersion
	);
}

function buildNativeVersionDeselectConfirmContent(
	removedVersions: string[],
	options: { label: string; value: string }[],
	patchName: string,
) {
	const patchLabel = patchName || '该热更版本';
	const versionLabels = removedVersions.map((nativeVersion) =>
		getNativeVersionOptionLabel(nativeVersion, options),
	);

	if (removedVersions.length === 1) {
		return (
			<>
				{`确认取消原生版本「${versionLabels[0]}」对热更版本「${patchLabel}」的分发绑定？`}
				<br />
				<Text type="secondary" style={{ fontSize: 12 }}>
					若已在 Pushy 发布，将同步取消对应分发；仅本地配置时仅从绑定列表移除。
				</Text>
			</>
		);
	}

	return (
		<>
			{`确认取消以下 ${removedVersions.length} 个原生版本对热更版本「${patchLabel}」的分发绑定？`}
			<ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
				{versionLabels.map((label) => (
					<li key={label}>{label}</li>
				))}
			</ul>
			<Text type="secondary" style={{ fontSize: 12 }}>
				若已在 Pushy 发布，将同步取消对应分发；仅本地配置时仅从绑定列表移除。
			</Text>
		</>
	);
}

type NativeVersionMultiSelectProps = {
	value: string[];
	options: { label: string; value: string }[];
	patchName?: string;
	loading?: boolean;
	disabled?: boolean;
	/** 是否已在 Pushy 发布（点过「应用」或有可取消的 bindingId） */
	hasPublishedBinding: (nativeVersion: string) => boolean;
	onChange: (values: string[]) => void;
	onDeselect: (
		removedVersions: string[],
		nextValues: string[],
	) => Promise<void>;
};

function NativeVersionMultiSelect({
	value,
	options,
	patchName = '',
	loading = false,
	disabled = false,
	hasPublishedBinding,
	onChange,
	onDeselect,
}: NativeVersionMultiSelectProps) {
	const allValues = useMemo(
		() => options.map((option) => option.value),
		[options],
	);
	const selectedCount = value.length;
	const isAllSelected =
		allValues.length > 0 && selectedCount === allValues.length;
	const isIndeterminate = selectedCount > 0 && !isAllSelected;

	const applySelectionChange = useCallback(
		(nextValues: string[]) => {
			const removedVersions = value.filter(
				(nativeVersion) => !nextValues.includes(nativeVersion),
			);

			if (removedVersions.length === 0) {
				onChange(nextValues);
				return;
			}

			const removedPublishedVersions = removedVersions.filter(
				hasPublishedBinding,
			);
			if (removedPublishedVersions.length === 0) {
				onChange(nextValues);
				return;
			}

			Modal.confirm({
				title: '确认取消原生版本绑定',
				content: buildNativeVersionDeselectConfirmContent(
					removedPublishedVersions,
					options,
					patchName,
				),
				okText: '确认取消',
				cancelText: '保留',
				okButtonProps: { danger: true },
				onOk: () => onDeselect(removedPublishedVersions, nextValues),
			});
		},
		[
			value,
			options,
			patchName,
			hasPublishedBinding,
			onChange,
			onDeselect,
		],
	);

	return (
		<Select
			mode="multiple"
			size="small"
			allowClear
			placeholder="请选择原生版本"
			value={value}
			options={options}
			loading={loading}
			disabled={disabled || loading}
			onChange={(selectedVersions) =>
				applySelectionChange(normalizeVersionList(selectedVersions))
			}
			popupRender={(menu) => (
				<>
					{options.length > 0 ? (
						<div
							style={{
								padding: '6px 12px',
								borderBottom: '1px solid rgba(5, 5, 5, 0.06)',
							}}
							onMouseDown={(event) => event.preventDefault()}
						>
							<Checkbox
								indeterminate={isIndeterminate}
								checked={isAllSelected}
								onChange={(event) =>
									applySelectionChange(
										event.target.checked ? [...allValues] : [],
									)
								}
							>
								全选
							</Checkbox>
						</div>
					) : null}
					{menu}
				</>
			)}
			style={{ width: '100%' }}
		/>
	);
}

type HotUpdateConfigPanelProps = {
	refreshKey?: number;
};

export default function HotUpdateConfigPanel({
	refreshKey = 0,
}: HotUpdateConfigPanelProps) {
	const [activeEnv, setActiveEnv] = useState<AppConfigEnv>('test');
	const [activePlatform, setActivePlatform] = useState<PushyPlatform>('ios');
	const [globalDrawerOpen, setGlobalDrawerOpen] = useState(false);
	const [draftRollouts, setDraftRollouts] = useState<
		Record<string, number | undefined>
	>({});
	const [rolloutOperatingKey, setRolloutOperatingKey] = useState<string | null>(
		null,
	);
	const [envStates, setEnvStates] = useState<Record<AppConfigEnv, EnvState>>({
		test: createInitialEnvState(),
		production: createInitialEnvState(),
	});
	const [nativePackageStates, setNativePackageStates] = useState(
		createInitialNativePackageStates,
	);

	const cosUploader = useMemo(
		() =>
			new BeLinkCOS({
				mode: 'production',
			}) as unknown as CosUploader,
		[],
	);

	const currentEnvConfig = useMemo(() => {
		return (
			APP_CONFIG_ENV_OPTIONS.find((item) => item.value === activeEnv) ||
			APP_CONFIG_ENV_OPTIONS[0]
		);
	}, [activeEnv]);

	const currentState = envStates[activeEnv];
	const hasChanges =
		serializeHotUpdate(currentState.hotUpdate) !==
		currentState.lastSavedHotUpdate;

	const updateEnvState = useCallback(
		(env: AppConfigEnv, updater: (state: EnvState) => EnvState) => {
			setEnvStates((prev) => ({
				...prev,
				[env]: updater(prev[env]),
			}));
		},
		[],
	);

	const updateCurrentHotUpdate = useCallback(
		(
			updater: (config: EditableHotUpdateManifest) => EditableHotUpdateManifest,
		) => {
			updateEnvState(activeEnv, (state) => ({
				...state,
				hotUpdate: updater(state.hotUpdate),
			}));
		},
		[activeEnv, updateEnvState],
	);

	const updateNativePackageState = useCallback(
		(
			env: AppConfigEnv,
			platform: PushyPlatform,
			updater: (state: NativePackageState) => NativePackageState,
		) => {
			setNativePackageStates((prev) => ({
				...prev,
				[env]: {
					...prev[env],
					[platform]: updater(prev[env][platform]),
				},
			}));
		},
		[],
	);

	const loadNativePackageOptions = useCallback(
		async (env: AppConfigEnv, platform: PushyPlatform, force = false) => {
			const current = nativePackageStates[env][platform];
			if (!force && (current.loaded || current.loading)) {
				return;
			}

			updateNativePackageState(env, platform, (state) => ({
				...state,
				loading: true,
				error: '',
			}));

			try {
				const [{ options, packages }, bindings] = await Promise.all([
					fetchPushyNativePackageOptions(env, platform),
					fetchPushyBindings(env, platform).catch(() => [] as PushyBindingRecord[]),
				]);
				updateNativePackageState(env, platform, (state) => ({
					...state,
					loading: false,
					loaded: true,
					options,
					packages,
					bindings,
					error: '',
				}));
			} catch (error) {
				const errorMessage = getErrorMessage(error);
				updateNativePackageState(env, platform, (state) => ({
					...state,
					loading: false,
					loaded: true,
					error: errorMessage,
				}));
				message.error(
					`读取 ${PLATFORM_META[platform].label} 原生版本失败：${errorMessage}`,
				);
			}
		},
		[nativePackageStates, updateNativePackageState],
	);

	const syncPushyHotUpdatePackages = useCallback(
		async (env: AppConfigEnv, platform: PushyPlatform) => {
			updateNativePackageState(env, platform, (state) => ({
				...state,
				loading: true,
				error: '',
			}));

			try {
				const [{ options, packages }, versions, bindings] = await Promise.all([
					fetchPushyNativePackageOptions(env, platform),
					fetchPushyHotUpdateVersions(env, platform),
					fetchPushyBindings(env, platform).catch(() => [] as PushyBindingRecord[]),
				]);

				updateNativePackageState(env, platform, (state) => ({
					...state,
					loading: false,
					loaded: true,
					options,
					packages,
					bindings,
					error: '',
				}));
				updateEnvState(env, (state) => ({
					...state,
					hotUpdate: {
						...state.hotUpdate,
						[platform]: {
							...state.hotUpdate[platform],
							items: buildHotUpdateItemsFromPushy(
								versions,
								packages,
								bindings,
								state.hotUpdate[platform].items,
							),
						},
					},
				}));
				return { success: true as const, count: versions.length };
			} catch (error) {
				const errorMessage = getErrorMessage(error);
				updateNativePackageState(env, platform, (state) => ({
					...state,
					loading: false,
					loaded: true,
					error: errorMessage,
				}));
				return { success: false as const, error: errorMessage };
			}
		},
		[updateEnvState, updateNativePackageState],
	);

	const syncAllPushyHotUpdatePackages = useCallback(
		async (env: AppConfigEnv) => {
			const results = await Promise.all(
				PUSHY_PLATFORMS.map(async (platform) => ({
					platform,
					result: await syncPushyHotUpdatePackages(env, platform),
				})),
			);

			const successItems = results.filter((item) => item.result.success);
			const failedItems = results.filter((item) => !item.result.success);

			if (successItems.length > 0) {
				const summary = successItems
					.map(
						(item) =>
							`${PLATFORM_META[item.platform].label} ${
								item.result.success ? item.result.count : 0
							} 条`,
					)
					.join('，');
				message.success(`三端同步完成：${summary}`);
			}

			if (failedItems.length > 0) {
				const summary = failedItems
					.map(
						(item) =>
							`${PLATFORM_META[item.platform].label}（${
								item.result.success ? '' : item.result.error
							}）`,
					)
					.join('，');
				message.error(`部分平台同步失败：${summary}`);
			}
		},
		[syncPushyHotUpdatePackages],
	);

	const getRolloutDraftKey = useCallback(
		(env: AppConfigEnv, platform: PushyPlatform, itemId: string) =>
			`${env}-${platform}-${itemId}`,
		[],
	);

	const handleApplyRollout = useCallback(
		async (
			platform: PushyPlatform,
			item: EditableHotUpdateItem,
			rollout: number | undefined,
		) => {
			const operatingKey = getRolloutDraftKey(activeEnv, platform, item.id);
			setRolloutOperatingKey(operatingKey);

			try {
				const nativePackageState = nativePackageStates[activeEnv][platform];
				const { appliedRollouts } = await applyPushyRolloutForItem(
					activeEnv,
					platform,
					item,
					rollout,
					nativePackageState.packages,
					nativePackageState.bindings,
				);
				const syncResult = await syncPushyHotUpdatePackages(activeEnv, platform);
				if (!syncResult.success) {
					throw new Error(syncResult.error);
				}
				const patchLabel = item.targetPatchName || '热更版本';
				if (appliedRollouts.length === 1) {
					message.success(
						`已将 ${patchLabel} 的灰度比例设为 ${appliedRollouts[0]}%`,
					);
				} else {
					message.success(`已应用 ${patchLabel} 的灰度配置`);
				}
			} catch (error) {
				message.error(`设置灰度失败：${getErrorMessage(error)}`);
			} finally {
				setRolloutOperatingKey(null);
			}
		},
		[
			activeEnv,
			getRolloutDraftKey,
			nativePackageStates,
			syncPushyHotUpdatePackages,
		],
	);

	const handleCancelPublish = useCallback(
		async (platform: PushyPlatform, item: EditableHotUpdateItem) => {
			const operatingKey = getRolloutDraftKey(activeEnv, platform, item.id);
			setRolloutOperatingKey(operatingKey);

			try {
				await cancelPushyPublishForItem(
					activeEnv,
					platform,
					item,
					nativePackageStates[activeEnv][platform].packages,
					nativePackageStates[activeEnv][platform].bindings,
				);
				const syncResult = await syncPushyHotUpdatePackages(activeEnv, platform);
				if (!syncResult.success) {
					throw new Error(syncResult.error);
				}
				message.success(`已取消 ${item.targetPatchName || '热更版本'} 的分发绑定`);
			} catch (error) {
				message.error(`取消发布失败：${getErrorMessage(error)}`);
			} finally {
				setRolloutOperatingKey(null);
			}
		},
		[
			activeEnv,
			getRolloutDraftKey,
			nativePackageStates,
			syncPushyHotUpdatePackages,
		],
	);

	const handleNativeVersionDeselect = useCallback(
		async (
			platform: PushyPlatform,
			item: EditableHotUpdateItem,
			index: number,
			removedVersions: string[],
			nextVersions: string[],
			optionLabels: { label: string; value: string }[],
		) => {
			const operatingKey = getRolloutDraftKey(activeEnv, platform, item.id);
			const nativePackageState = nativePackageStates[activeEnv][platform];
			setRolloutOperatingKey(operatingKey);

			try {
				await cancelPushyPublishForNativeVersions(
					activeEnv,
					platform,
					item,
					removedVersions,
					nativePackageState.packages,
					nativePackageState.bindings,
				);
				const syncResult = await syncPushyHotUpdatePackages(activeEnv, platform);
				if (!syncResult.success) {
					throw new Error(syncResult.error);
				}

				updateEnvState(activeEnv, (state) => {
					const items = [...state.hotUpdate[platform].items];
					items[index] = {
						...items[index],
						targetNativeVersions: nextVersions,
						enabled: nextVersions.length > 0,
					};
					return {
						...state,
						hotUpdate: {
							...state.hotUpdate,
							[platform]: { ...state.hotUpdate[platform], items },
						},
					};
				});

				const removedLabels = removedVersions.map((nativeVersion) =>
					getNativeVersionOptionLabel(nativeVersion, optionLabels),
				);
				message.success(
					removedLabels.length === 1
						? `已取消原生版本「${removedLabels[0]}」的热更新绑定`
						: `已取消 ${removedLabels.length} 个原生版本的热更新绑定`,
				);
			} catch (error) {
				message.error(`取消绑定失败：${getErrorMessage(error)}`);
				throw error;
			} finally {
				setRolloutOperatingKey(null);
			}
		},
		[
			activeEnv,
			getRolloutDraftKey,
			nativePackageStates,
			syncPushyHotUpdatePackages,
			updateEnvState,
		],
	);

	const loadEnvConfig = useCallback(
		async (env: AppConfigEnv) => {
			const envConfig =
				APP_CONFIG_ENV_OPTIONS.find((item) => item.value === env) ||
				APP_CONFIG_ENV_OPTIONS[0];

			updateEnvState(env, (state) => ({ ...state, loading: true }));

			try {
				const response = await fetch(`${envConfig.url}?t=${Date.now()}`, {
					cache: 'no-store',
				});
				if (!response.ok) {
					throw new Error(`读取失败，状态码：${response.status}`);
				}

				const data = (await response.json()) as RemoteConfig;
				const hotUpdate = normalizeHotUpdateManifest(data.pushyHotUpdate);

				updateEnvState(env, (state) => ({
					...state,
					loading: false,
					loaded: true,
					lastModified: formatYmdHmsZh(
						response.headers.get('Last-Modified') || '',
					),
					fullConfig: data,
					hotUpdate,
					lastSavedHotUpdate: serializeHotUpdate(hotUpdate),
				}));
			} catch (error) {
				updateEnvState(env, (state) => ({ ...state, loading: false }));
				message.error(
					`读取${envConfig.label}热更新配置失败：${getErrorMessage(error)}`,
				);
			}
		},
		[updateEnvState],
	);

	useEffect(() => {
		if (!currentState.loaded && !currentState.loading) {
			void loadEnvConfig(activeEnv);
		}
	}, [activeEnv, currentState.loaded, currentState.loading, loadEnvConfig]);

	useEffect(() => {
		if (refreshKey > 0) {
			void (async () => {
				await loadEnvConfig(activeEnv);
				await syncAllPushyHotUpdatePackages(activeEnv);
			})();
		}
	}, [activeEnv, loadEnvConfig, refreshKey, syncAllPushyHotUpdatePackages]);

	useEffect(() => {
		for (const platform of PUSHY_PLATFORMS) {
			void loadNativePackageOptions(activeEnv, platform);
		}
	}, [activeEnv, loadNativePackageOptions]);

	const handleSave = useCallback(async () => {
		const validationMessage = validateHotUpdate(currentState.hotUpdate);
		if (validationMessage) {
			message.error(validationMessage);
			return;
		}
		if (!currentState.fullConfig) {
			message.error('还没有读取到完整配置，无法保存');
			return;
		}

		updateEnvState(activeEnv, (state) => ({ ...state, saving: true }));

		try {
			const nextHotUpdate = buildManifestForSave(currentState.hotUpdate);
			const nextConfig: RemoteConfig = {
				...currentState.fullConfig,
				pushyHotUpdate: nextHotUpdate,
			};

			await uploadTextToCosPath(cosUploader, {
				path: new URL(currentEnvConfig.url).pathname,
				body: formatConfigForUpload(nextConfig),
				contentType: 'application/json; charset=utf-8',
			});

			const savedHotUpdate = normalizeHotUpdateManifest(nextHotUpdate);

			updateEnvState(activeEnv, (state) => ({
				...state,
				saving: false,
				loaded: true,
				lastModified: formatYmdHmsZh(new Date()),
				fullConfig: nextConfig,
				hotUpdate: savedHotUpdate,
				lastSavedHotUpdate: serializeHotUpdate(savedHotUpdate),
			}));
			message.success(`${currentEnvConfig.label}热更新配置保存成功`);
		} catch (error) {
			updateEnvState(activeEnv, (state) => ({ ...state, saving: false }));
			message.error(`保存失败：${getErrorMessage(error)}`);
		}
	}, [
		activeEnv,
		cosUploader,
		currentEnvConfig.label,
		currentEnvConfig.url,
		currentState.fullConfig,
		currentState.hotUpdate,
		updateEnvState,
	]);

	const renderPlatformItems = (platform: PushyPlatform) => {
		const platformConfig = currentState.hotUpdate[platform];
		const nativePackageState = nativePackageStates[activeEnv][platform];
		const meta = PLATFORM_META[platform];

		const columns: ColumnsType<EditableHotUpdateItem> = [
			{
				title: '热更版本',
				dataIndex: 'targetPatchName',
				width: 140,
				render: (value: string) =>
					value ? (
						<Tag color="purple" style={{ fontSize: 13, margin: 0 }}>
							{value}
						</Tag>
					) : (
						<Text type="secondary">-</Text>
					),
			},
			{
				title: '绑定的原生版本',
				width: 280,
				dataIndex: 'targetNativeVersions',
				render: (_versions: string[], item, index) => {
					const nativeVersionOptions = buildNativeVersionSelectOptions(
						item,
						nativePackageState.options,
						nativePackageState.packages,
						nativePackageState.bindings,
					);
					const nativeBindings = getItemNativeBindings(
						item,
						nativePackageState.packages,
						nativePackageState.bindings,
					);
					const hasPublishedBinding = (nativeVersion: string) =>
						!!nativeBindings[nativeVersion]?.bindingId;

					return (
						<NativeVersionMultiSelect
							value={item.targetNativeVersions}
							options={nativeVersionOptions}
							patchName={item.targetPatchName}
							hasPublishedBinding={hasPublishedBinding}
							loading={nativePackageState.loading}
							disabled={
								nativePackageState.loading ||
								rolloutOperatingKey ===
									getRolloutDraftKey(activeEnv, platform, item.id)
							}
							onChange={(targetNativeVersions) =>
								updateCurrentHotUpdate((config) => {
									const items = [...config[platform].items];
									items[index] = {
										...items[index],
										targetNativeVersions,
										enabled: targetNativeVersions.length > 0,
									};
									return {
										...config,
										[platform]: { ...config[platform], items },
									};
								})
							}
							onDeselect={(removedVersions, nextVersions) =>
								handleNativeVersionDeselect(
									platform,
									item,
									index,
									removedVersions,
									nextVersions,
									nativeVersionOptions,
								)
							}
						/>
					);
				},
			},
			{
				title: '灰度比例',
				width: 180,
				render: (_value: unknown, item) => {
					const draftKey = getRolloutDraftKey(activeEnv, platform, item.id);
					const bindings = getItemNativeBindings(
						item,
						nativePackageState.packages,
						nativePackageState.bindings,
					);
					const draftRollout = draftRollouts[draftKey];
					const isOperating = rolloutOperatingKey === draftKey;
					const hasCancelableBinding = item.targetNativeVersions.some(
						(nativeVersion) => !!bindings[nativeVersion]?.bindingId,
					);
					const hasNativeVersions = item.targetNativeVersions.length > 0;
					const hasUnpublishedVersions = hasUnpublishedNativeVersions(
						item,
						nativePackageState.packages,
						nativePackageState.bindings,
					);
					const canApply = canApplyRollout(
						item,
						draftRollout,
						nativePackageState.packages,
						nativePackageState.bindings,
					);

					return (
						<Space direction="vertical" size={4} style={{ width: '100%' }}>
							<InputNumber
								size="small"
								min={0}
								max={100}
								step={1}
								precision={0}
								value={draftRollout}
								placeholder={
									hasUnpublishedVersions
										? `新增默认 ${getDefaultNewNativeVersionRollout(platform)}%`
										: '请输入'
								}
								onChange={(value) =>
									setDraftRollouts((prev) => ({
										...prev,
										[draftKey]: normalizeRolloutInput(value),
									}))
								}
								addonAfter="%"
								style={{ width: '100%' }}
								disabled={isOperating}
							/>
							<Space size={4} wrap={false}>
								<Button
									size="small"
									type="primary"
									loading={isOperating}
									disabled={isOperating || !canApply}
									onClick={() =>
										void handleApplyRollout(platform, item, draftRollout)
									}
								>
									应用
								</Button>
								<Popconfirm
									title="确认取消该热更版本的全部分发绑定？"
									okText="确认"
									cancelText="取消"
									onConfirm={() => void handleCancelPublish(platform, item)}
									disabled={isOperating || !hasNativeVersions}
								>
									<Button
										size="small"
										danger
										disabled={
											isOperating ||
											!hasNativeVersions ||
											!hasCancelableBinding
										}
									>
										取消发布
									</Button>
								</Popconfirm>
							</Space>
						</Space>
					);
				},
			},
			{
				title: '更新模式',
				dataIndex: 'updateMode',
				width: 400,
				render: (mode: UpdateMode, _item, index) => (
					<Select
						size="small"
						value={mode}
						options={UPDATE_MODE_OPTIONS}
						onChange={(updateMode) =>
							updateCurrentHotUpdate((config) => {
								const items = [...config[platform].items];
								items[index] = { ...items[index], updateMode };
								return {
									...config,
									[platform]: { ...config[platform], items },
								};
							})
						}
						style={{ width: '100%' }}
					/>
				),
			},
			// {
			// 	title: '创建时间',
			// 	dataIndex: 'createdAt',
			// 	width: 170,
			// 	render: (value: string) =>
			// 		value ? (
			// 			<Text type="secondary" style={{ fontSize: 12 }}>
			// 				{new Date(value).toLocaleString('zh-CN', { hour12: false })}
			// 			</Text>
			// 		) : (
			// 			<Text type="secondary">-</Text>
			// 		),
			// },
			{
				title: '状态',
				dataIndex: 'enabled',
				width: 90,
				render: (enabled: boolean) =>
					enabled ? (
						<Tag color="green" style={{ margin: 0 }}>
							使用中
						</Tag>
					) : (
						<Tag style={{ margin: 0 }}>未使用</Tag>
					),
			},
		];

		return (
			<Card
				key={platform}
				size="small"
				styles={{ body: { padding: 16 } }}
				title={
					<Space size={8}>
						<Tag color={meta.color} style={{ margin: 0 }}>
							{meta.label}
						</Tag>
						<Text strong>平台配置</Text>
					</Space>
				}
				extra={
					<Space>
						<Button
							type="primary"
							icon={<SyncOutlined />}
							loading={nativePackageState.loading}
							onClick={() =>
								void syncPushyHotUpdatePackages(activeEnv, platform)
							}
						>
							同步 Pushy 数据
						</Button>
						<Tooltip title="平台总开关">
							<Switch
								checked={platformConfig.enabled}
								checkedChildren="启用"
								unCheckedChildren="停用"
								onChange={(enabled) =>
									updateCurrentHotUpdate((config) => ({
										...config,
										[platform]: {
											...config[platform],
											enabled,
										},
									}))
								}
							/>
						</Tooltip>
					</Space>
				}
			>
				<Space direction="vertical" size="middle" style={{ width: '100%' }}>
					{nativePackageState.error ? (
						<Alert
							type="warning"
							showIcon
							message="Pushy 数据读取失败"
							description={nativePackageState.error}
						/>
					) : null}

					<Row gutter={[12, 8]} align="middle">
						<Col xs={24} md={8}>
							<Space
								direction="vertical"
								size={4}
								style={{ width: '100%' }}
							>
								<Text strong>检查间隔(秒)</Text>
								<InputNumber
									min={0}
									placeholder="继承全局"
									value={platformConfig.checkIntervalSeconds}
									onChange={(value) =>
										updateCurrentHotUpdate((config) => ({
											...config,
											[platform]: {
												...config[platform],
												checkIntervalSeconds: normalizeNumberInput(value),
											},
										}))
									}
									style={{ width: '100%' }}
								/>
							</Space>
						</Col>
						<Col xs={24} md={16}>
							<Space
								direction="vertical"
								size={4}
								style={{ width: '100%' }}
							>
								<Text strong>强制检查 Token</Text>
								<Input
									allowClear
									placeholder="继承全局；修改后会触发一次检查"
									value={platformConfig.forceCheckToken}
									onChange={(event) =>
										updateCurrentHotUpdate((config) => ({
											...config,
											[platform]: {
												...config[platform],
												forceCheckToken: event.target.value,
											},
										}))
									}
									style={{ width: '100%' }}
								/>
							</Space>
						</Col>
					</Row>

					<Table
						size="middle"
						rowKey="id"
						pagination={false}
						dataSource={platformConfig.items}
						columns={columns}
						scroll={{ x: 'max-content' }}
						expandable={{
							expandedRowRender: (item) => {
								const itemIndex = platformConfig.items.findIndex(
									(entry) => entry.id === item.id,
								);
								if (itemIndex < 0) {
									return null;
								}
								return (
									<Row gutter={[12, 8]} align="middle">
										<Col xs={24} md={10}>
											<Space
												direction="vertical"
												size={4}
												style={{ width: '100%' }}
											>
												<Text strong>单项检查间隔(秒)</Text>
												<InputNumber
													min={0}
													placeholder="继承平台/全局"
													value={item.checkIntervalSeconds}
													onChange={(value) =>
														updateCurrentHotUpdate((config) => {
															const items = [...config[platform].items];
															items[itemIndex] = {
																...items[itemIndex],
																checkIntervalSeconds:
																	normalizeNumberInput(value),
															};
															return {
																...config,
																[platform]: {
																	...config[platform],
																	items,
																},
															};
														})
													}
													style={{ width: '100%' }}
												/>
											</Space>
										</Col>
										<Col xs={24} md={14}>
											<Space
												direction="vertical"
												size={4}
												style={{ width: '100%' }}
											>
												<Text strong>单项强制检查 Token</Text>
												<Input
													allowClear
													placeholder="继承平台/全局；修改后会触发一次检查"
													value={item.forceCheckToken}
													onChange={(event) =>
														updateCurrentHotUpdate((config) => {
															const items = [...config[platform].items];
															items[itemIndex] = {
																...items[itemIndex],
																forceCheckToken: event.target.value,
															};
															return {
																...config,
																[platform]: {
																	...config[platform],
																	items,
																},
															};
														})
													}
													style={{ width: '100%' }}
												/>
											</Space>
										</Col>
									</Row>
								);
							},
							rowExpandable: () => true,
						}}
						locale={{
							emptyText: (
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description="暂无热更新包，点击「同步 Pushy 数据」获取最新列表"
								/>
							),
						}}
					/>
				</Space>
			</Card>
		);
	};

	const renderEnvPanel = () => (
		<Spin spinning={currentState.loading} tip="读取热更新配置中...">
			<Space
				direction="vertical"
				size="middle"
				style={{ width: '100%', paddingBottom: 88 }}
			>
				<Descriptions
					size="small"
					bordered
					column={{ xs: 1, sm: 1, md: 2 }}
					items={[
						{
							key: 'env',
							label: '当前环境',
							children: (
								<Tag color={currentEnvConfig.tagColor} style={{ margin: 0 }}>
									{currentEnvConfig.label}
								</Tag>
							),
						},
						{
							key: 'modified',
							label: '最近读取/保存',
							children: (
								<Text type="secondary">
									{currentState.lastModified || '-'}
								</Text>
							),
						},
						{
							key: 'url',
							label: '配置地址',
							span: 2,
							children: (
								<Paragraph
									copyable={{ text: currentEnvConfig.url }}
									style={{
										marginBottom: 0,
										fontFamily:
											'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
										fontSize: 12,
									}}
								>
									{currentEnvConfig.url}
								</Paragraph>
							),
						},
					]}
				/>

				<Tabs
					type="card"
					activeKey={activePlatform}
					onChange={(key) => setActivePlatform(key as PushyPlatform)}
					tabBarExtraContent={
						<Space>
							<Button
								type="primary"
								icon={<SyncOutlined />}
								loading={PUSHY_PLATFORMS.some(
									(platform) => nativePackageStates[activeEnv][platform].loading,
								)}
								onClick={() => void syncAllPushyHotUpdatePackages(activeEnv)}
							>
								同步三端 Pushy 数据
							</Button>
							<Button
								icon={<SettingOutlined />}
								onClick={() => setGlobalDrawerOpen(true)}
							>
								全局设置
							</Button>
						</Space>
					}
					items={PUSHY_PLATFORMS.map((platform) => {
						const meta = PLATFORM_META[platform];
						return {
							key: platform,
							label: (
								<Text style={{ fontSize: 15 }} strong>
									{meta.label}
								</Text>
							),
							children: renderPlatformItems(platform),
						};
					})}
				/>

				<Drawer
					title="全局热更新设置"
					placement="right"
					width={420}
					open={globalDrawerOpen}
					onClose={() => setGlobalDrawerOpen(false)}
				>
					<Space direction="vertical" size="large" style={{ width: '100%' }}>
						<Alert
							type="info"
							showIcon
							message="平台未填写时，将继承这里的默认值；保存只会覆盖 config.json 中的 pushyHotUpdate 字段。"
						/>

						<Space direction="vertical" size={8} style={{ width: '100%' }}>
							<Text strong>总开关</Text>
							<Switch
								checked={currentState.hotUpdate.enabled}
								checkedChildren="启用"
								unCheckedChildren="停用"
								onChange={(enabled) =>
									updateCurrentHotUpdate((config) => ({ ...config, enabled }))
								}
							/>
						</Space>

						<Space direction="vertical" size={8} style={{ width: '100%' }}>
							<Text strong>默认检查间隔（秒）</Text>
							<InputNumber
								min={0}
								value={currentState.hotUpdate.checkIntervalSeconds}
								onChange={(value) =>
									updateCurrentHotUpdate((config) => ({
										...config,
										checkIntervalSeconds: normalizeNumberInput(value),
									}))
								}
								style={{ width: '100%' }}
							/>
						</Space>

						<Space direction="vertical" size={8} style={{ width: '100%' }}>
							<Text strong>默认强制检查 Token</Text>
							<Input
								allowClear
								placeholder="例如：force-update-check-1"
								value={currentState.hotUpdate.forceCheckToken}
								onChange={(event) =>
									updateCurrentHotUpdate((config) => ({
										...config,
										forceCheckToken: event.target.value,
									}))
								}
							/>
						</Space>
					</Space>
				</Drawer>

				<Affix offsetBottom={0}>
					<div
						style={{
							zIndex: 10,
							padding: '12px 20px',
							background: 'var(--ant-color-bg-container, #fff)',
							borderTop: '1px solid var(--ant-color-split, #f0f0f0)',
							boxShadow: '0 -6px 16px rgba(0,0,0,0.06)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: 16,
							flexWrap: 'wrap',
						}}
					>
						<Space size="small">
							{hasChanges ? (
								<Text type="warning" strong>
									⚠ 有未保存修改
								</Text>
							) : (
								<Text type="secondary">所有修改已保存</Text>
							)}
							{currentState.lastModified ? (
								<Text type="secondary">
									· 上次时间 {currentState.lastModified}
								</Text>
							) : null}
						</Space>
						<Space>
							<Button
								icon={<ReloadOutlined />}
								onClick={() => void loadEnvConfig(activeEnv)}
								loading={currentState.loading}
								disabled={currentState.saving}
							>
								重新读取
							</Button>
							<Button
								type="primary"
								icon={<SaveOutlined />}
								onClick={() => void handleSave()}
								loading={currentState.saving}
								disabled={currentState.loading || !currentState.loaded}
							>
								保存配置
							</Button>
						</Space>
					</div>
				</Affix>
			</Space>
		</Spin>
	);

	return (
		<Tabs
			activeKey={activeEnv}
			onChange={(key) => setActiveEnv(key as AppConfigEnv)}
			items={APP_CONFIG_ENV_OPTIONS.map((option) => ({
				key: option.value,
				label: <Text style={{ fontSize: 16 }}>{option.label}</Text>,
				children: renderEnvPanel(),
			}))}
		/>
	);
}
